import { useState } from 'react';
import { useApiQuery } from '@/hooks/useApiQuery';

interface AuditLog {
  id: number;
  userName: string;
  userEmail: string;
  userRole: string;
  action: string;
  entityType: string;
  entityName: string;
  ipAddress: string;
  requestMethod: string;
  requestPath: string;
  statusCode: number;
  createdAt: string;
}

export default function AuditLogs() {
  const { data: logs, loading } = useApiQuery<AuditLog[]>({ endpoint: '/audit-logs' });
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('all');

  const filtered = logs?.filter((l) => {
    const matchesSearch = (l.userName || '').toLowerCase().includes(search.toLowerCase()) ||
      (l.action || '').toLowerCase().includes(search.toLowerCase()) ||
      (l.entityName || '').toLowerCase().includes(search.toLowerCase());
    const matchesAction = actionFilter === 'all' || l.action === actionFilter;
    return matchesSearch && matchesAction;
  });

  const actionColors: Record<string, string> = {
    create: 'bg-emerald-50 text-emerald-700',
    update: 'bg-sky-50 text-sky-700',
    delete: 'bg-red-50 text-red-700',
    login: 'bg-brand-50 text-brand-700',
    logout: 'bg-stone-100 text-stone-700',
  };

  return (
    <div className="p-4 md:p-8 min-h-screen">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-stone-900">Audit Logs</h1>
        <p className="text-stone-500 text-sm mt-1">Complete tamper-evident audit trail of all system actions.</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-stone-100 p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 max-w-md">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by user, action, or entity..."
              className="w-full border border-stone-200 rounded-lg pl-10 pr-4 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {['all', 'create', 'update', 'delete', 'login'].map((a) => (
              <button
                key={a}
                onClick={() => setActionFilter(a)}
                className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors capitalize whitespace-nowrap ${
                  actionFilter === a ? 'bg-brand-600 text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                }`}
              >
                {a === 'all' ? 'All Actions' : a}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-stone-100 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <i className="ri-loader-4-line text-brand-600 text-2xl animate-spin" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-stone-50 border-b border-stone-100">
                <tr>
                  <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Timestamp</th>
                  <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">User</th>
                  <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Action</th>
                  <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Entity</th>
                  <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">IP Address</th>
                  <th className="px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {filtered?.map((log) => (
                  <tr key={log.id} className="hover:bg-stone-50 transition-colors">
                    <td className="px-4 py-3 text-stone-700 text-sm whitespace-nowrap">
                      {new Date(log.createdAt).toLocaleString()}
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-stone-900 text-sm font-medium">{log.userName}</p>
                      <p className="text-stone-400 text-xs">{log.userRole}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${actionColors[log.action] || 'bg-stone-100 text-stone-700'}`}>
                        {log.action}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-stone-900 text-sm">{log.entityName}</p>
                      <p className="text-stone-400 text-xs">{log.entityType}</p>
                    </td>
                    <td className="px-4 py-3 text-stone-500 text-sm font-mono">{log.ipAddress}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        log.statusCode >= 200 && log.statusCode < 300 ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
                      }`}>
                        {log.statusCode}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filtered?.length === 0 && (
              <div className="p-8 text-center">
                <i className="ri-article-line text-stone-300 text-3xl mb-2" />
                <p className="text-stone-400 text-sm">No audit logs found.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}